import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { readerSettingsService, type ReaderSettingDto } from '../api/readerSettingsService';
import type { ReaderSettings } from '../components/reader/types';
import { LayoutMode, PageAnimation, PageMargin, TapZoneMode } from '../components/reader/types';

const USE_BOOK_STYLES_KEY = 'reader-useBookStyles';

/**
 * Convert backend ReaderSettingDto to frontend ReaderSettings type
 * Includes useBookStyles from localStorage
 */
function mapDtoToSettings(dto: ReaderSettingDto): ReaderSettings {
  // Get useBookStyles from localStorage (defaults to true = use book's original styles)
  const storedValue = localStorage.getItem(USE_BOOK_STYLES_KEY);
  const useBookStyles = storedValue === null ? true : storedValue === 'true';

  return {
    theme: dto.theme as 'light' | 'dark',
    fontSize: dto.fontSize,
    fontFamily: dto.fontFamily,
    lineHeight: dto.lineHeight,
    backgroundColor: dto.backgroundColor,
    textColor: dto.textColor,
    autoHideToolbar: dto.autoHideToolbar,
    autoHideDelay: dto.autoHideDelayMs,
    useBookStyles,
    // New layout fields — null coalesce to enum 0 (Auto/Slide/Medium/Auto defaults)
    layoutMode: (dto.layoutMode ?? 0) as LayoutMode,
    pageAnimation: (dto.pageAnimation ?? 0) as PageAnimation,
    pageMargin: (dto.pageMargin ?? 0) as PageMargin,
    tapZoneMode: (dto.tapZoneMode ?? 0) as TapZoneMode,
  };
}

/**
 * Hook to manage reader settings (global per user, not per book)
 */
export function useReaderSettings() {
  const queryClient = useQueryClient();
  const queryKey = ['readerSettings'];

  // Fetch reader settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const dto = await readerSettingsService.getMySettings();
      return mapDtoToSettings(dto);
    },
    staleTime: Infinity, // Settings rarely change, cache indefinitely
  });

  // Update reader settings mutation
  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<ReaderSettings>) => {
      // Handle useBookStyles separately (localStorage only)
      if (updates.useBookStyles !== undefined) {
        localStorage.setItem(USE_BOOK_STYLES_KEY, String(updates.useBookStyles));
      }

      // Merge with current settings
      const currentSettings = settings || {
        theme: 'light' as const,
        fontSize: 16,
        fontFamily: 'Arial',
        lineHeight: 1.6,
        backgroundColor: '#FFFFFF',
        textColor: '#000000',
        autoHideToolbar: false,
        autoHideDelay: 3000,
        useBookStyles: true, // Default: use book's original styles
        layoutMode: LayoutMode.Auto,
        pageAnimation: PageAnimation.Slide,
        pageMargin: PageMargin.Medium,
        tapZoneMode: TapZoneMode.Auto,
      };

      const mergedSettings = { ...currentSettings, ...updates };

      // Only send backend fields to API (exclude useBookStyles)
      const dto = await readerSettingsService.updateMySettings({
        theme: mergedSettings.theme,
        fontSize: mergedSettings.fontSize,
        fontFamily: mergedSettings.fontFamily,
        lineHeight: mergedSettings.lineHeight,
        backgroundColor: mergedSettings.backgroundColor,
        textColor: mergedSettings.textColor,
        autoHideToolbar: mergedSettings.autoHideToolbar,
        autoHideDelayMs: mergedSettings.autoHideDelay,
        layoutMode: mergedSettings.layoutMode,
        pageAnimation: mergedSettings.pageAnimation,
        pageMargin: mergedSettings.pageMargin,
        tapZoneMode: mergedSettings.tapZoneMode,
      });

      return mapDtoToSettings(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateSettings.mutateAsync,
    isUpdating: updateSettings.isPending,
  };
}
